import { useState, useEffect } from 'react';
import { useForm } from '@tanstack/react-form';
import type { AnyFieldApi } from '@tanstack/react-form';
import { z } from 'zod';
import { RadixButton as Button, RadixCard as Card, Heading, Text, RadixTextField as TextField } from '@printtat/ui';
import { Link, useNavigate, useSearchParams } from 'react-router-dom';
import { Alert } from '@printtat/ui';

const resetPasswordSchema = z.object({
  new_password: z
    .string()
    .min(8, 'Password must be at least 8 characters')
    .regex(/[A-Z]/, 'Password must contain at least one uppercase letter')
    .regex(/[a-z]/, 'Password must contain at least one lowercase letter')
    .regex(/[0-9]/, 'Password must contain at least one number')
    .regex(/[@$!%*?&]/, 'Password must contain at least one special character'),
  confirm_password: z.string(),
}).refine((data) => data.new_password === data.confirm_password, {
  message: "Passwords don't match",
  path: ['confirm_password'],
});

const formatFieldErrors = (errors: readonly unknown[]): string =>
  errors
    .map((err) =>
      typeof err === 'string'
        ? err
        : (err as { message?: string })?.message ?? '',
    )
    .filter(Boolean)
    .join(', ');

export const ResetPasswordPage = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token') || undefined;

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<{
    type: 'success' | 'error';
    message: string;
  } | null>(null);

  useEffect(() => {
    if (!token) {
      setSubmitStatus({
        type: 'error',
        message: 'Invalid or missing reset token. Please request a new password reset.',
      });
    }
  }, [token]);

  const form = useForm({
    defaultValues: {
      new_password: '',
      confirm_password: '',
    },
    validators: {
      onChange: resetPasswordSchema,
    },
    onSubmit: async ({ value }) => {
      if (!token) {
        setSubmitStatus({
          type: 'error',
          message: 'Invalid or missing reset token.',
        });
        return;
      }

      setIsSubmitting(true);
      setSubmitStatus(null);

      try {
        const apiUrl = import.meta.env.VITE_API_URL || 'http://localhost:4000';
        const response = await fetch(`${apiUrl}/auth/reset-password`, {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
          },
          body: JSON.stringify({
            token,
            newPassword: value.new_password,
          }),
        });

        const data = await response.json();

        if (response.ok) {
          setSubmitStatus({
            type: 'success',
            message: data.message || 'Password reset successful! You can now sign in with your new password.',
          });

          // Redirect to sign in after 3 seconds
          setTimeout(() => {
            navigate('/sign-in');
          }, 3000);
        } else {
          throw new Error(data.message || 'Failed to reset password');
        }
      } catch (error) {
        setSubmitStatus({
          type: 'error',
          message: error instanceof Error ? error.message : 'An error occurred. Please try again.',
        });
      } finally {
        setIsSubmitting(false);
      }
    },
  });

  return (
    <div className="min-h-screen flex items-center justify-center bg-gray-50 py-12 px-4 sm:px-6 lg:px-8">
      <div className="w-full max-w-md">
        <Card size="4">
          <div className="text-center mb-8">
            <Heading size="8" weight="bold" className="text-center mb-2">
              Create New Password
            </Heading>
            <Text size="3" color="gray" className="text-center">
              Enter your new password below.
            </Text>
          </div>

          {submitStatus && (
            <Alert variant={submitStatus.type} className="mb-6">
              {submitStatus.message}
            </Alert>
          )}

          {!submitStatus || submitStatus.type === 'error' ? (
            <form
              onSubmit={(e) => {
                e.preventDefault();
                e.stopPropagation();
                form.handleSubmit();
              }}
            >
              <div className="space-y-6">
                <form.Field name="new_password">
                  {(field: AnyFieldApi) => (
                    <div>
                      <label
                        htmlFor={field.name}
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        New Password
                      </label>
                      <TextField.Root
                        id={field.name}
                        name={field.name}
                        type="password"
                        placeholder="Enter new password"
                        value={field.state.value as string}
                        onChange={(e) => field.handleChange(e.target.value)}
                        onBlur={field.handleBlur}
                        disabled={isSubmitting || !token}
                        size="3"
                      />
                      {field.state.meta.errors.length > 0 && (
                        <Text size="1" color="red" className="mt-1 block">
                          {formatFieldErrors(field.state.meta.errors)}
                        </Text>
                      )}
                    </div>
                  )}
                </form.Field>

                <form.Field name="confirm_password">
                  {(field: AnyFieldApi) => (
                    <div>
                      <label
                        htmlFor={field.name}
                        className="block text-sm font-medium text-gray-700 mb-1"
                      >
                        Confirm New Password
                      </label>
                      <TextField.Root
                        id={field.name}
                        name={field.name}
                        type="password"
                        placeholder="Confirm new password"
                        value={field.state.value as string}
                        onChange={(e) => field.handleChange(e.target.value)}
                        onBlur={field.handleBlur}
                        disabled={isSubmitting || !token}
                        size="3"
                      />
                      {field.state.meta.errors.length > 0 && (
                        <Text size="1" color="red" className="mt-1 block">
                          {formatFieldErrors(field.state.meta.errors)}
                        </Text>
                      )}
                    </div>
                  )}
                </form.Field>

                <Button
                  type="submit"
                  size="3"
                  radius="full"
                  className="w-full"
                  disabled={isSubmitting || !token}
                  loading={isSubmitting}
                >
                  {isSubmitting ? 'Resetting Password...' : 'Reset Password'}
                </Button>

                <div className="text-center">
                  <Link
                    to="/sign-in"
                    className="text-sm text-blue-600 hover:text-blue-500 font-medium"
                  >
                    Back to Sign In
                  </Link>
                </div>
              </div>
            </form>
          ) : (
            <div className="text-center">
              <Text size="2" color="gray" className="block mb-4">
                Redirecting to sign in...
              </Text>
              <Link to="/sign-in">
                <Button size="3" radius="full" variant="soft" className="w-full">
                  Sign In Now
                </Button>
              </Link>
            </div>
          )}
        </Card>
      </div>
    </div>
  );
};
